import { TableIds, useTableStore } from "@/store/tables";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { arrayMove } from "@dnd-kit/sortable";
import { Table } from "@tanstack/react-table";
import { ReactNode, useCallback } from "react";

interface DndSortableHeadersContextProps<T> {
  table: Table<T>;
  children: ReactNode;
  tableId: TableIds;
}

export const DndSortableHeadersContext = <T,>({
  table,
  children,
  tableId,
}: DndSortableHeadersContextProps<T>) => {
  const { setTableSettings, getTableSettings } = useTableStore((state) => ({
    setTableSettings: state.setTableSettings,
    getTableSettings: state.getTableSettings,
  }));
  const savedSettings = getTableSettings(tableId);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active && over && active.id !== over.id) {
        const oldIndex = table.getState().columnOrder.indexOf(active.id as string);
        const newIndex = table.getState().columnOrder.indexOf(over.id as string);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = arrayMove(table.getState().columnOrder, oldIndex, newIndex);

          const defaultSettings = { columnVisibility: {}, columnOrder: [] };

          setTableSettings(tableId, {
            ...defaultSettings,
            ...savedSettings,
            columnOrder: newOrder,
          });
        }
      }
    },
    [table, tableId, savedSettings, setTableSettings]
  );
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      {children}
    </DndContext>
  );
};
